import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { privacyPolicy } from "../../content/privacy.policy.mjs";
import { reservationPolicy } from "../../content/reservation.policy.mjs";
import { termsAndConditions } from "../../content/terms.and.conditions";
import { editorialPages } from "../../pages/company/editorial.content";
import shareImage from "../../assets/images/desktop/consign-hero-v2.png";
import teamImage from "../../assets/images/desktop/careers-team.png";
import workshopImage from "../../assets/images/showroom/showroom-banner.png";

const pages = {
  "/": { title: "Buy Cars in Malaysia", description: "Explore DCAR vehicle listings, compare cars and find your next car in Malaysia. Discover vehicle consignment and automotive services." },
  "/consign": { title: "Sell Your Car", description: "Start your vehicle consignment journey with DCAR. Tell us about your car and explore support with listings, viewings and selling." },
  "/workshop": { title: "Our Panel Workshops", description: "Explore DCAR panel workshops in Malaysia, find location and contact details, and connect with our automotive experts.", image: workshopImage },
  "/profile": { title: "My Profile", description: "Manage your DCAR profile, saved cars, referral information and account details.", private: true },
  "/purchases": { title: "My Purchases", description: "Follow your DCAR purchase journey, review payments and documents, and track your vehicle handover.", private: true },
  "/consignments": { title: "My Consignments", description: "View your vehicle consignment records and follow the progress of selling your car with DCAR.", private: true },
  "/about-us": { title: "About Us & Contact", description: "Meet DCAR, operated by DCH Automobile Sdn Bhd. Contact our team by phone or email and find our location in Bandar Bukit Raja, Klang." },
  ...Object.fromEntries([privacyPolicy, reservationPolicy, termsAndConditions].map(policy => [
    policy === termsAndConditions ? "/terms-and-conditions" : policy.route,
    { title: policy.title, description: policy.summary },
  ])),
  ...Object.fromEntries(Object.entries(editorialPages).map(([path, page]) => [path, {
    title: { journal: "Blog & Car Stories", careers: "Careers", insurance: "Motor Insurance", partners: "Dealership Program" }[page.type],
    description: page.description,
    image: page.type === "careers" ? teamImage : undefined,
  }])),
};

export function getPageMetadata(pathname, product, loading = false) {
  const path = pathname.replace(/\/+$/, "") || "/";
  const canonicalPath = { "/contact-us": "/about-us", "/terms-of-use": "/terms-and-conditions" }[path] || path;
  const match = canonicalPath.match(/^\/(car|car-plate)\/([^/]+)$/);
  let page = pages[canonicalPath] || pages["/"];
  if (match) {
    const currentProduct = !loading && String(product?.id) === decodeURIComponent(match[2]) ? product : null;
    const isPlate = match[1] === "car-plate";
    page = {
      title: currentProduct?.name || (isPlate ? "Car Plate Details" : "Vehicle Details"),
      description: currentProduct?.name
        ? `Explore ${currentProduct.name} on DCAR. ${isPlate ? "View car plate availability and reservation details." : "View vehicle photos, specifications and availability, and enquire about your next car."}`
        : `Explore ${isPlate ? "car plate availability and reservation details" : "vehicle photos, specifications and availability"} on DCAR.`,
      image: currentProduct?.media?.[0]?.url,
    };
  }
  return { ...page, title: `${page.title} | DCAR`, path: canonicalPath };
}

export default function PageMetadata() {
  const { pathname } = useLocation();
  const { productInfo, productInfoLoading } = useSelector(state => state.product);
  const page = getPageMetadata(pathname, productInfo, productInfoLoading);
  const base = (process.env.REACT_APP_URL || "https://dcar.my").replace(/\/+$/, "");
  const url = `${base}${page.path}`;
  const image = new URL(page.image || shareImage, base).href;
  return <Helmet>
    <title>{page.title}</title>
    <meta name="description" content={page.description} />
    <meta name="robots" content={page.private ? "noindex, nofollow" : "index, follow"} />
    <link rel="canonical" href={url} />
    <meta property="og:title" content={page.title} />
    <meta property="og:description" content={page.description} />
    <meta property="og:url" content={url} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="DCAR" />
    <meta property="og:image" content={image} />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={page.title} />
    <meta name="twitter:description" content={page.description} />
    <meta name="twitter:image" content={image} />
  </Helmet>;
}
